import { action, makeObservable, observable } from 'mobx';
import { DateRange, DateRangeType } from 'src/lib/types';
import { BaseStore } from './baseStore';
import { RootStore } from './rootStore';

export interface DateStoreProps {
  /**
   * Date range type. One of 'Weekly', 'Monthly', or 'Yearly'
   */
  rangeType: DateRangeType;
  /**
   * The current date
   */
  currentDate: Date;
  /**
   * Date range values
   */
  dateRange?: DateRange;
}

export class DateStore extends BaseStore implements DateStoreProps {
  public rangeType = DateRangeType.Yearly;
  public currentDate = new Date();
  public dateRange?: DateRange = {};

  constructor(rootStore: RootStore) {
    super(rootStore);

    makeObservable(this, {
      rangeType: observable,
      currentDate: observable,
      dateRange: observable,
      setRangeType: action,
      calculateRange: action,
      setCurrentDate: action,
      modifyCurrentDate: action,
    });

    // Calculate initial date range
    this.calculateRange(this.currentDate, this.rangeType);
  }

  /**
   * Checks to see if a range type is allowed.
   *
   * @param rangeType - Range type to check
   * @returns Boolean indicating if the type is valid
   */
  private isValidType = (rangeType: DateRangeType) => Object.values(DateRangeType).includes(rangeType);

  /**
   * Updates stored range type.
   *
   * @param rangeType - New date range type
   */
  public setRangeType = (rangeType: DateRangeType) => {
    if (!this.isValidType(rangeType)) return;

    // Store new range type
    this.rangeType = rangeType;
  };

  /**
   * Calculates and saves a new date range.
   *
   * @remarks
   * Runs automatically when service is first loaded.
   *
   * @param currentDate - Current date to use. Defaults to stored `currentDate` if not provided
   * @param rangeType - Updated range type. Defaults to stored `rangeType` if not provided
   * @returns The new date range (or undefined if all params were undefined)
   */
  public calculateRange = (currentDate?: Date, rangeType?: DateRangeType) => {
    currentDate = currentDate ? currentDate : this.currentDate;
    rangeType = rangeType ? rangeType : this.rangeType;
    if (!currentDate || !rangeType) return;

    const newRange: DateRange = {};

    if (rangeType === DateRangeType.Yearly) {
      newRange.start = new Date(currentDate.getFullYear(), 0, 1);
      newRange.end = new Date(currentDate.getFullYear() + 1, 0, 0);
    } else if (rangeType === DateRangeType.Monthly) {
      newRange.start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      newRange.end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    } else if (rangeType === DateRangeType.Weekly) {
      const weekStart = currentDate.getDate() - currentDate.getDay();
      const weekEnd = weekStart + 6;
      newRange.start = new Date(currentDate.getFullYear(), currentDate.getMonth(), weekStart);
      newRange.end = new Date(currentDate.getFullYear(), currentDate.getMonth(), weekEnd);
    }

    // Store current date
    newRange.currentDate = currentDate;

    // Store new date range
    this.dateRange = newRange;

    // Return data
    return newRange;
  };

  /**
   * Sets the current date.
   *
   * @param currentDate - New current date
   */
  public setCurrentDate = (currentDate: Date) => {
    this.currentDate = currentDate;
  };

  /**
   * Moves the current date forward or back by a given number of weeks, months, or years.
   *
   * @remarks
   * Whether the date is moved by weeks/months/years depends on the date range type.
   *
   * @param action - Number of units to add or remove from the current date. Can be negative
   * @param currentDate - Current date to use. Defaults to stored `currentDate` if not provided
   * @param rangeType - Date range type to use. Defaults to stored `rangeType` if not provided
   * @returns The new current date
   */
  public modifyCurrentDate = (modifier: number, currentDate?: Date, rangeType?: DateRangeType) => {
    currentDate = currentDate ? currentDate : this.currentDate;
    rangeType = rangeType ? rangeType : this.rangeType;
    if (!modifier || !currentDate || !rangeType) return;

    let newDate = currentDate;

    // Get new current date
    if (rangeType === DateRangeType.Weekly) {
      newDate.setDate(newDate.getDate() + modifier * 7);
      newDate = new Date(newDate);
    } else if (rangeType === DateRangeType.Monthly) {
      newDate.setMonth(newDate.getMonth() + modifier);
      newDate = new Date(newDate);
    } else if (rangeType === DateRangeType.Yearly) {
      newDate.setFullYear(newDate.getFullYear() + modifier);
      newDate = new Date(newDate);
    }

    // Store new date
    this.currentDate = newDate;

    return newDate;
  };
}
